import { SelectOption } from '@globaleris/ui.library'
import { AppConfigurationKey } from '../../../../model/enums/appConfigurationKey'
import { getAppConfigurationValue } from '../../../../redux'
import AxiosInstance from '../../../http-config'
import { EnumCurrency } from '../../../Pioneer'
import { EnumCommodity } from '../../enums'
import { EnumClientTypes } from '../../enums/enumClientTypes'
import { EnumConceptType } from '../../enums/enumConceptType'
import {
  Concept,
  ConceptLayoutConfigurationModel,
  ConceptModelRequest,
  ConceptPatchRequest,
  ConceptPriceModelRequest,
  ConceptQuantityTypeOptionsModel
} from './model/concepts'

export const apiGetConcepts = async (
  currency: EnumCurrency | undefined,
  commodity: EnumCommodity | undefined,
  companyId: number,
  clientType: EnumClientTypes
): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  if (commodity === undefined) {
    return await AxiosInstance.get(
      `${apiSuffixUrl}/api/Concept/FetchConcepts?currency=${currency}&companyId=${companyId}&clientType=${clientType}`
    ).then(response => {
      if (response.data) {
        return response.data
      } else {
        return []
      }
    })
  } else {
    return await AxiosInstance.get(
      `${apiSuffixUrl}/api/Concept/FetchConcepts?currency=${currency}&commodity=${commodity}&companyId=${companyId}&clientType=${clientType}`
    ).then(response => {
      if (response.data) {
        return response.data
      } else {
        return []
      }
    })
  }
}

export const apiFetchConceptTable = async ({
  clientType,
  commodity,
  companyId,
  includeInactives,
  lgBypass
}: {
  clientType: number
  commodity: number
  companyId?: number
  includeInactives?: boolean | null
  lgBypass?: boolean | null
}): Promise<Concept[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const params = {
    clientType: clientType,
    commodity: commodity,
    companyId: companyId,
    includeInactives: includeInactives,
    lgBypass: lgBypass
  }
  return await AxiosInstance.get(`${apiSuffixUrl}/api/Concept/FetchConceptsBySubjectType`, {
    params: params
  }).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiFetchConceptTypesByClientType = async (
  clientType: EnumClientTypes
): Promise<SelectOption[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Concept/FetchConceptTypesByClientType?clientType=${clientType}`
  ).then(response => {
    return response.data
  })
}

export const apiFetchIVATypes = async (
  clientType: EnumClientTypes,
  conceptType: EnumConceptType | undefined
): Promise<SelectOption[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  if (conceptType) {
    return await AxiosInstance.get(
      `${apiSuffixUrl}/api/Concept/FetchIVATypes?clientType=${clientType}&conceptType=${conceptType}`
    ).then(response => {
      return response.data
    })
  } else {
    return []
  }
}

export const apiGetConceptByID = async (id: number): Promise<Concept> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.get(`${apiSuffixUrl}/api/Concept/${id}`).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

/***
 * apiGetBaseConceptByID
 * This function will fetch the concept by its ID with conceptPrices unrelated to any idContrato/idProducto/etc etc
 * @param id - The ID of the concept
 * @returns The concept with base ConceptPrices
 * */
export const apiGetBaseConceptByID = async (id: number): Promise<Concept> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.get(`${apiSuffixUrl}/api/Concept/${id}/base`).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiPostConcept = async (request: ConceptModelRequest): Promise<Concept> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.post(`${apiSuffixUrl}/api/Concept/CreateConcept`, request).then(
    response => {
      if (response.data) {
        return response.data
      } else {
        return []
      }
    }
  )
}

export const apiUpdateConcept = async (
  request: ConceptModelRequest | ConceptPatchRequest
): Promise<Concept> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.post(`${apiSuffixUrl}/api/Concept/UpdateConcept`, request).then(
    response => {
      if (response.data) {
        return response.data
      } else {
        return []
      }
    }
  )
}

export const apiFetchLayoutConfiguration = async (
  calculationType: number
): Promise<ConceptLayoutConfigurationModel> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Concept/layoutConfiguration?calculationType=${calculationType}`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiFetchQuantityTypeOptions = async (
  calculationType: number
): Promise<ConceptQuantityTypeOptionsModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Concept/QuantityTypeOptions?calculationType=${calculationType}`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiCreateConceptPrice = async (
  conceptPriceModel: ConceptPriceModelRequest,
  version: string = '2.0'
): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/ConceptPrices?version=${version}`,
    conceptPriceModel
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return null
    }
  })
}

export const apiDeleteConceptPrice = async (
  conceptPriceId: number,
  version: string = '2.0'
): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.delete(
    `${apiSuffixUrl}/api/ConceptPrices/${conceptPriceId}?version=${version}`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return null
    }
  })
}

export const apiPatchConceptPrice = async (
  conceptId: number,
  conceptPriceId: number,
  request: any,
  version: string = '2.0'
): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.patch(
    `${apiSuffixUrl}/api/Concept/${conceptId}/ConceptPrice/${conceptPriceId}`,
    request
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return null
    }
  })
}

export const apiGetRelatedConcepts = async (conceptId: number): Promise<Concept[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.get(`${apiSuffixUrl}/api/Concept/relatedConcepts/${conceptId}`).then(
    response => {
      if (response.data) {
        return response.data
      } else {
        return []
      }
    }
  )
}

export const apiLinkRelationalConcept = async (request: {
  conceptId: number
  relationalConceptId: number
}): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/concept/LinkRelationalConcept`,
    request
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return null
    }
  })
}

export const apiDeleteLinkRelationalConcept = async (request: {
  conceptId: number
  relationalConceptId: number
}): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/concept/DeleteLinkRelationalConcept`,
    request
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return null
    }
  })
}

export const apiUpdateLinkRelationalConcept = async (request: {
  conceptId: number
  relationalConceptId: number
  ivaTypeConceptId: number
}): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/concept/UpdateLinkRelationalConcept`,
    request
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return null
    }
  })
}

export const apiGetAggregatorConcepts = async ({
  clientType,
  commodity,
  companyId,
  includeInactives
}: {
  clientType: number
  commodity: number
  companyId?: number
  includeInactives?: boolean | null
}): Promise<SelectOption[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const params = {
    clientType,
    commodity,
    companyId,
    includeInactives
  }
  return await AxiosInstance.get(`${apiSuffixUrl}/api/Concept/AggregatorConcepts`, {
    params
  }).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiGetNonAggregatorConcepts = async ({
  clientType,
  commodity,
  companyId,
  includeInactives
}: {
  clientType: number
  commodity: number
  companyId?: number
  includeInactives?: boolean | null
}): Promise<SelectOption[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const params = {
    clientType,
    commodity,
    companyId,
    includeInactives
  }
  return await AxiosInstance.get(`${apiSuffixUrl}/api/Concept/NonAggregatorConcepts`, {
    params
  }).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}
